import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bell, ChevronRight, Pill, AlertTriangle, Package } from "lucide-react";
import { useSanitarioAuth } from "../../context/SanitarioAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SanitarioTerapie() {
  const { token } = useSanitarioAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/oss/me/terapie-extra/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setAlerts(data.items || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Terapie e Farmaci</h1>
        <p className="text-sm text-slate-500 mt-1">
          Promemoria somministrazioni, alert e accesso rapido alla scheda terapie dell&apos;ospite.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/sanitario/scheda"
          className="flex items-center gap-3 rounded-2xl border border-sky-200 bg-white p-4 shadow-sm hover:shadow-md transition"
        >
          <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center">
            <Pill className="w-5 h-5 text-sky-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800">Apri scheda ospite</p>
            <p className="text-xs text-slate-500">Terapie attive e somministrazioni</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
        <Link
          to="/sanitario/deblistering"
          className="flex items-center gap-3 rounded-2xl border border-violet-200 bg-white p-4 shadow-sm hover:shadow-md transition"
        >
          <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center">
            <Package className="w-5 h-5 text-violet-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800">Deblistering</p>
            <p className="text-xs text-slate-500">Controlli farmaci e blister</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-orange-500" />
          <h2 className="font-bold text-slate-800">Alert terapie</h2>
          {alerts.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-orange-500 text-white text-xs font-bold">
              {alerts.length}
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Nessun alert al momento.</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((a, i) => (
              <li key={a.id || a.terapia_extra_id || i}>
                <Link
                  to={
                    a.fascicolo_id
                      ? `/sanitario/scheda/${a.fascicolo_id}`
                      : "/sanitario/scheda"
                  }
                  className="flex items-start gap-3 rounded-xl bg-slate-50 hover:bg-sky-50 px-3 py-3 transition"
                >
                  {a.missing || a.mancante ? (
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  ) : (
                    <Pill className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {a.farmaco || a.nome || "Terapia"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {a.ospite_nome || a.nome_completo || "Ospite"}
                      {a.scheduled_at
                        ? ` · ${new Date(a.scheduled_at).toLocaleString("it-IT", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`
                        : ""}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
